import React, { useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  Box,
  Typography,
  Rating,
  Stack,
  Paper,
} from "@mui/material";
import { useStyle } from "../../context/styleContext/styleContext";
import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import shoppingCartIcon from "../../images/shopping_cart.svg";

export default function HomePageProductCard({ productData }) {
  const { addToCart } = useContext(ShoppingCartContext);
  const { style } = useStyle();

  if (!productData) return null;

  const addProductToCart = (e) => {
    e.preventDefault();
    addToCart(productData.SKU);
  };

  const formatPrice = (price) => Number(price).toFixed(2);
  const isFree = Number(productData.price) <= 0;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        backgroundColor: style.darkPanel,
        border: "1px solid rgba(255,255,255,0.08)",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          p: 1.25,
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        <Stack
          spacing={1.1}
          sx={{
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            height: "100%",
            flex: 1,
          }}
        >
          <Box
            component={RouterLink}
            to={`/products/${productData.productURI}`}
            sx={{
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
              height: 210,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
              borderRadius: "12px",
              backgroundColor: style.darkerPanel,
              border: "1px solid rgba(255,255,255,0.06)",
              p: 1,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={productData.images?.[0]?.URI}
              alt={productData.images?.[0]?.altText}
              sx={{
                width: productData.productURI === "d1-delay" ? "92%" : "78%",
                maxWidth: "100%",
                maxHeight: 180,
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>

          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            <Stack spacing={0.8} sx={{ minWidth: 0, maxWidth: "100%" }}>
              <Typography
                component={RouterLink}
                to={`/products/${productData.productURI}`}
                sx={{
                  color: style.color4,
                  textDecoration: "none",
                  fontWeight: 800,
                  fontSize: "1.15rem",
                  lineHeight: 1.15,
                  minHeight: "2.65em",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  "&:hover": {
                    color: style.color3,
                  },
                }}
              >
                {productData.name}
              </Typography>

              <Box
                component={RouterLink}
                to={`/reviews/${productData.productURI}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                  textDecoration: "none",
                  color: "inherit",
                  width: "fit-content",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <Rating
                  value={Number(productData.starRating || 0)}
                  precision={0.5}
                  readOnly
                  size="small"
                />
                <Typography sx={{ fontWeight: 700, color: style.color4 }}>
                  {Number(productData.starRating || 0) > 0 ? productData.starRating : ""}
                </Typography>
                <Typography
                  sx={{
                    color: style.color3,
                    fontSize: "0.9rem",
                    wordBreak: "break-word",
                  }}
                >
                  ({productData.ratingCount || 0} review{Number(productData.ratingCount) === 1 ? "" : "s"})
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.55,
                  minHeight: 44,
                  maxHeight: 44,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  fontSize: "0.92rem",
                  wordBreak: "break-word",
                }}
              >
                {productData.cardSummary}
              </Typography>

              {isFree ? (
                <Box sx={{ minHeight: 44, pt: 0.25 }}>
                  <Typography
                    sx={{
                      color: style.color4,
                      fontSize: "1.25rem",
                      lineHeight: 1.2,
                    }}
                  >
                    <strong style={{ color: "rgb(0,200,50)" }}>FREE</strong>{" "}
                    <span style={{ fontSize: "0.9rem", color: style.color3 }}>
                      (limited time)
                    </span>
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ minHeight: 44, pt: 0.25 }}>
                  <Typography
                    sx={{
                      color: style.color3,
                      fontSize: "1rem",
                      lineHeight: 1.1,
                    }}
                  >
                    <span style={{ textDecoration: "line-through" }}>
                      ${formatPrice(productData.msrp)}
                    </span>
                  </Typography>

                  <Typography
                    sx={{
                      color: style.color4,
                      fontWeight: 800,
                      fontSize: "1.4rem",
                      lineHeight: 1.1,
                    }}
                  >
                    ${formatPrice(productData.price)}
                  </Typography>
                </Box>
              )}
            </Stack>

            <Box
              sx={{
                mt: "auto",
                pt: 1.1,
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              {isFree ? (
                <Button
                  component={RouterLink}
                  to={`/products/${productData.productURI}`}
                  sx={{
                    width: "100%",
                    borderRadius: "5px",
                    textTransform: "none",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: style.accentGreen,
                    "&:hover": {
                      backgroundColor: style.accentGreen,
                      filter: "brightness(1.05)",
                    },
                  }}
                >
                  Get Free Plugin
                </Button>
              ) : (
                <Stack spacing={1} sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
                  <Button
                    onClick={addProductToCart}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 900,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                      width: "100%",
                      "&:hover": {
                        backgroundColor: style.accentGreen,
                        filter: "brightness(1.05)",
                      },
                    }}
                  >
                    <img
                      src={shoppingCartIcon}
                      alt="Shopping cart icon"
                      style={{ maxWidth: "18px", marginRight: "6px" }}
                    />
                    Add To Cart
                  </Button>

                  <Button
                    component={RouterLink}
                    to="/subscribe"
                    startIcon={<BoltRoundedIcon />}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 900,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      width: "100%",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.10)",
                      },
                    }}
                  >
                    Try for Free
                  </Button>

                  <Button
                    component={RouterLink}
                    to={`/products/${productData.productURI}`}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "transparent",
                      border: "1px solid rgba(255,255,255,0.10)",
                      width: "100%",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.04)",
                      },
                    }}
                  >
                    <ArrowForwardRoundedIcon sx={{ fontSize: "1.25rem", mr: 0.5 }} />
                    Learn More
                  </Button>
                </Stack>
              )}
            </Box>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}